/**
 * ErrorBoundary.jsx — React Error Boundary
 *
 * Catches JavaScript errors anywhere in the child component tree and displays
 * a friendly fallback UI instead of a blank white screen.
 *
 * Why a class component?
 *   React Error Boundaries must be class components — the lifecycle methods
 *   componentDidCatch and getDerivedStateFromError are not available as hooks.
 *
 * Usage in App.js:
 *   <ErrorBoundary>
 *     <MyPage />
 *   </ErrorBoundary>
 *
 * Optional props:
 *   fallback  {ReactNode} — custom fallback UI (defaults to built-in card)
 *   onError   {function}  — called with (error, errorInfo) for external logging
 */

import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import './ErrorBoundary.css';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError:   false,
      error:      null,
      errorInfo:  null,
    };
  }

  // ── Lifecycle: capture error into state ─────────────────────────────────────
  static getDerivedStateFromError(error) {
    // Update state so the next render shows the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log to console so developers can see the full stack in DevTools
    console.error('ErrorBoundary caught a render error:', error, errorInfo);

    this.setState({ errorInfo });

    // If the parent passed an onError handler (e.g. Sentry/logging service), call it
    if (typeof this.props.onError === 'function') {
      this.props.onError(error, errorInfo);
    }
  }

  // ── Recover: reset state so the component tree re-mounts ────────────────────
  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    const { hasError, error } = this.state;
    const { fallback, children } = this.props;

    if (hasError) {
      // If consumer provided a custom fallback, render that
      if (fallback) return fallback;

      // Default fallback card
      return (
        <div className="eb-overlay">
          <div className="eb-card">
            <div className="eb-icon">
              <AlertTriangle size={28} />
            </div>
            <h2 className="eb-title">Something went wrong</h2>
            <p className="eb-body">
              An unexpected error occurred in this part of Querely. Your session and
              data are safe — this view failed to render.
            </p>
            {/* Show the error message in development for easier debugging */}
            {process.env.NODE_ENV !== 'production' && error && (
              <pre className="eb-detail">{error.toString()}</pre>
            )}
            <div className="eb-actions">
              <button className="eb-btn eb-btn--primary" onClick={this.handleReset}>
                <RefreshCw size={15} />
                Try Again
              </button>
              <button
                className="eb-btn eb-btn--ghost"
                onClick={() => window.location.reload()}
              >
                Reload Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return children;
  }
}

export default ErrorBoundary;
